import * as nifti from "nifti-reader-js";
import { MODALITY } from "../classes/enums/MedicalImagesEnum";
import { dbDateFormat } from "../classes/utils/DateUtils";

/**
 * Service for extracting metadata from NIfTI files.
 * Uses the same orientation calculation logic as NiftiViewer for consistency.
 */
class NiftiService {
  /**
   * Reads a NIfTI file and extracts metadata
   * @param {File} file - The NIfTI file to read
   * @returns {Promise<object>} - Promise resolving to the extracted metadata
   */
  static async readMetadata(file) {
    return new Promise(resolve => {
      try {
        const fallbackMetadata = this.createFallbackMetadata(file);
        const reader = new FileReader();

        reader.onload = e => {
          try {
            if (!nifti || typeof nifti.isNIFTI !== "function") {
              resolve(fallbackMetadata);
              return;
            }

            let buffer = e.target.result;

            // Handle compression
            if (nifti.isCompressed(buffer)) {
              try {
                buffer = nifti.decompress(buffer);
              } catch (decompressError) {
                resolve(fallbackMetadata);
                return;
              }
            }

            // Validate NIfTI file
            if (!nifti.isNIFTI(buffer)) {
              resolve(fallbackMetadata);
              return;
            }

            const header = nifti.readHeader(buffer);

            // NIfTI version: default to 1; upgrade to 2 if header is a NIFTI2 instance
            let niftiVersion = 1;
            if (typeof nifti.NIFTI2 !== "undefined" && header instanceof nifti.NIFTI2) {
              niftiVersion = 2;
            }

            // Use the same dimension adjustment logic as viewer
            const dimensions = this.adjustDimensions(header);
            const maxDimension = Math.max(dimensions[0] || 0, dimensions[1] || 0, dimensions[2] || 0);
            const pixelDimensions = this.extractPixelDimensions(header);
            const modality = this.determineModality(header, file.name);

            // Use the improved orientation calculation
            const orientationResult = this.calculateNiftiOrientation(
              header,
              modality
            );

            // Generate metadata
            const dimString = dimensions.map(d => d.toString()).join("x");
            const seriesId = `nifti_${file.name.replace(
              /\s/g,
              "_"
            )}_${dimString}`;
            const creationDate =
              this.extractDate(header, file) ||
              dbDateFormat(new Date(file.lastModified));

            const metadata = {
              creationDate,
              modality,
              orientation: orientationResult.orientationString,
              seriesId,
              studyId: seriesId,
              instanceId: `${seriesId}_${Date.now()}`,
              dimensions: {
                x: dimensions[0],
                y: dimensions[1],
                z: dimensions[2],
                t:
                  header.dims[0] >= 4 && header.dims[4] > 1
                    ? header.dims[4]
                    : null,
              },
              pixelDimensions: {
                x: pixelDimensions[0],
                y: pixelDimensions[1],
                z: pixelDimensions[2],
              },
              description: header.description
                ? header.description.trim()
                : null,
              dataType: this.getDataTypeName(header.datatypeCode),
              niftiVersion,
              maxDimension,
            };

            resolve(metadata);
          } catch (error) {
            resolve(fallbackMetadata);
          }
        };

        reader.onerror = error => {
          resolve(fallbackMetadata);
        };

        reader.readAsArrayBuffer(file);
      } catch (error) {
        resolve(this.createFallbackMetadata(file));
      }
    });
  }

  /**
   * Creates fallback metadata when parsing fails
   * @param {File} file - The NIfTI file
   * @returns {object} - Basic metadata
   */
  static createFallbackMetadata(file) {
    const modality = this.determineModalityFromFilename(file.name);
    return {
      creationDate: dbDateFormat(new Date(file.lastModified || Date.now())),
      modality: modality,
      orientation: null,
      seriesId: `nifti_${file.name.replace(/\s/g, "_")}`,
      studyId: `nifti_study_${Date.now().toString().substring(0, 8)}`,
      instanceId: `nifti_instance_${Date.now()}`,
      dimensions: { x: 256, y: 256, z: null, t: null },
      pixelDimensions: { x: 1.0, y: 1.0, z: 1.0 },
      niftiVersion: 1,
      maxDimension: 256,
    };
  }

  /**
   * Adjusts dimensions using the same logic as NiftiViewer
   * @param {object} header - The NIfTI header
   * @returns {number[]} - Array of dimensions [x, y, z]
   */
  static adjustDimensions(header) {
    const d1 = header.dims[1];
    const d2 = header.dims[2];
    const d3 = header.dims[3];
    return [d1, d2, d3];
  }

  /**
   * Normalizes a 3D vector
   * @param {number[]} vec - Vector to normalize
   * @returns {number[]} - Normalized vector
   */
  static normalizeVector(vec) {
    const magnitude = Math.sqrt(vec[0] ** 2 + vec[1] ** 2 + vec[2] ** 2);
    if (magnitude === 0) return vec;
    return vec.map(v => v / magnitude);
  }

  /**
   * Determines anatomical direction from a vector
   * @param {number[]} vec - Direction vector
   * @returns {string} - Anatomical direction (L/R, A/P, I/S)
   */
  static getAnatomicalDirection(vec) {
    const abs = vec.map(Math.abs);
    const maxIndex = abs.indexOf(Math.max(...abs));

    // RAS convention: +X=Right, +Y=Anterior, +Z=Superior
    const directions = [
      vec[0] > 0 ? "R" : "L", // X-axis
      vec[1] > 0 ? "A" : "P", // Y-axis
      vec[2] > 0 ? "S" : "I", // Z-axis
    ];

    return directions[maxIndex];
  }

  /**
   * Calculates NIfTI orientation with improved robustness
   * @param {object} header - The NIfTI header
   * @param {string} modality - The modality code
   * @returns {object} - Orientation info
   */
  static calculateNiftiOrientation(header, modality) {
    let affine = null;

    // Get the affine transformation matrix
    if (header.sform_code > 0 && header.affine) {
      affine = header.affine;
    } else if (
      header.qform_code > 0 &&
      header.quatern_b !== undefined &&
      header.quatern_c !== undefined &&
      header.quatern_d !== undefined
    ) {
      // Convert quaternion to rotation matrix
      const b = header.quatern_b || 0;
      const c = header.quatern_c || 0;
      const d = header.quatern_d || 0;
      const a = Math.sqrt(Math.max(0, 1.0 - (b * b + c * c + d * d)));

      const R = [
        [
          a * a + b * b - c * c - d * d,
          2 * (b * c - a * d),
          2 * (b * d + a * c),
        ],
        [
          2 * (b * c + a * d),
          a * a + c * c - b * b - d * d,
          2 * (c * d - a * b),
        ],
        [
          2 * (b * d - a * c),
          2 * (c * d + a * b),
          a * a + d * d - b * b - c * c,
        ],
      ];

      // Apply voxel size scaling and qfac
      const [dx, dy, dzRaw] = header.pixdim.slice(1, 4);
      const qfac = header.pixdim[0] || 1;
      const dz = dzRaw * qfac;

      // Build full affine matrix with translation
      affine = [
        [R[0][0] * dx, R[0][1] * dy, R[0][2] * dz, header.qoffset_x || 0],
        [R[1][0] * dx, R[1][1] * dy, R[1][2] * dz, header.qoffset_y || 0],
        [R[2][0] * dx, R[2][1] * dy, R[2][2] * dz, header.qoffset_z || 0],
        [0, 0, 0, 1],
      ];
    } else {
      const [dx, dy, dz] = header.pixdim.slice(1, 4);
      affine = [
        [dx || 1, 0, 0, 0],
        [0, dy || 1, 0, 0],
        [0, 0, dz || 1, 0],
        [0, 0, 0, 1],
      ];
    }

    // Extract and normalize direction vectors from affine columns
    const xDirection = this.normalizeVector([
      affine[0][0],
      affine[1][0],
      affine[2][0],
    ]);
    const yDirection = this.normalizeVector([
      affine[0][1],
      affine[1][1],
      affine[2][1],
    ]);
    const zDirection = this.normalizeVector([
      affine[0][2],
      affine[1][2],
      affine[2][2],
    ]);

    // Get anatomical orientation for each voxel axis
    const rowOrientation = this.getAnatomicalDirection(xDirection);
    const colOrientation = this.getAnatomicalDirection(yDirection);
    const sliceOrientation = this.getAnatomicalDirection(zDirection);

    // Format orientation string based on modality
    let orientationString;

    if (
      modality === MODALITY.DX ||
      modality === MODALITY.CR
    ) {
      // For 2D X-rays, only use row and column
      orientationString = JSON.stringify({
        row_left: rowOrientation,
        column_bottom: colOrientation,
      });
    } else {
      // For 3D volumes (CT, MR)
      orientationString = JSON.stringify({
        row_left: rowOrientation,
        column_bottom: colOrientation,
        first_slice: sliceOrientation,
      });
    }

    return {
      orientationString: orientationString,
      referenceSystem: [rowOrientation, colOrientation, sliceOrientation],
      affine: affine,
    };
  }

  /**
   * Extracts pixel dimensions from the NIfTI header
   * @param {object} header - The NIfTI header
   * @returns {number[]} - Array of pixel dimensions [x, y, z]
   */
  static extractPixelDimensions(header) {
    if (!header.pixdim) return [1.0, 1.0, 1.0];
    return [
      header.pixdim[1] || 1.0,
      header.pixdim[2] || 1.0,
      header.pixdim[3] || 1.0,
    ];
  }

  /**
   * Attempts to extract a date from the NIfTI header or filename
   * @param {object} header - The NIfTI header
   * @param {File} file - The NIfTI file
   * @returns {string|null} - Formatted date or null
   */
  static extractDate(header, file) {
    // Try to extract from description
    if (header.description) {
      const desc = header.description.trim();
      const dateMatch = desc.match(/\d{4}[-/]\d{2}[-/]\d{2}/);
      if (dateMatch) {
        return dateMatch[0].replace(/\//g, "-");
      }
    }

    // Try to extract from filename
    const filename = file.name;

    // YYYYMMDD format
    let dateMatch = filename.match(/\d{8}/);
    if (dateMatch) {
      const dateStr = dateMatch[0];
      return `${dateStr.substring(0, 4)}-${dateStr.substring(
        4,
        6
      )}-${dateStr.substring(6, 8)}`;
    }

    // YYYY-MM-DD format
    dateMatch = filename.match(/\d{4}[-_]\d{2}[-_]\d{2}/);
    if (dateMatch) {
      return dateMatch[0].replace(/_/g, "-");
    }

    return null;
  }

  /**
   * Determines modality based on header and filename
   * @param {object} header - The NIfTI header
   * @param {string} filename - The name of the file
   * @returns {string} - The modality code
   */
  static determineModality(header, filename) {
    // First check header description
    if (header.description) {
      const desc = header.description.toLowerCase();
      if (desc.includes("mri") || desc.includes("t1") || desc.includes("t2"))
        return MODALITY.MR;
      if (desc.includes("ct")) return MODALITY.CT;
      if (desc.includes("pet")) return MODALITY.PT;
      if (
        desc.includes("xray") ||
        desc.includes("radiograph") ||
        desc.includes("dx")
      )
        return MODALITY.DX;
      if (desc.includes("cr")) return MODALITY.CR;
    }

    // Dimensions are a stronger signal than filename — check first.
    const zDim = header.dims ? header.dims[3] : null;
    const numDims = header.dims ? header.dims[0] : 3;
    const isSingleSlice = numDims <= 2 || zDim === 1;

    // Filename heuristics: DX/CR are only valid for single-slice files;
    // CT/MR/PT/US are valid for any dimensionality.
    const fromFilename = this.determineModalityFromFilename(filename);
    if (fromFilename !== null) {
      const isDxOrCr = fromFilename === MODALITY.DX || fromFilename === MODALITY.CR;
      if (!isDxOrCr || isSingleSlice) return fromFilename;
    }

    // Structural fallback: single z-slice → DX, multi-slice → user decides.
    if (isSingleSlice) return MODALITY.DX;

    // 3D with no clues: user decides in ImageValidation
    return null;
  }

  /**
   * Determines modality from filename
   * @param {string} filename - The name of the file
   * @returns {string} - The modality code
   */
  static determineModalityFromFilename(filename) {
    const lowerFilename = filename.toLowerCase();
    // Split on common filename delimiters for whole-token matching.
    // "mr", "cr", "us" are too short to safely match as substrings —
    // e.g. "mr" hits in study IDs, "us" hits in "scout", "cr" hits in "sacrum".
    const tokens = lowerFilename.split(/[-_.\s]+/);

    if (lowerFilename.includes("ct")) return MODALITY.CT;
    if (
      tokens.includes("mr") ||
      tokens.includes("mri") ||
      tokens.includes("t1") ||
      tokens.includes("t2")
    )
      return MODALITY.MR;
    if (lowerFilename.includes("pet")) return MODALITY.PT;
    if (tokens.includes("us")) return MODALITY.US;
    if (tokens.includes("cr")) return MODALITY.CR;
    if (
      lowerFilename.includes("xray") ||
      lowerFilename.includes("radiograph") ||
      tokens.includes("xr") ||
      tokens.includes("dx")
    )
      return MODALITY.DX;

    // Cannot determine modality from filename alone
    return null;
  }

  /**
   * Gets the name of a NIfTI data type from its code
   * @param {number} code - The data type code
   * @returns {string} - Data type name
   */
  static getDataTypeName(code) {
    if (!nifti || !nifti.NIFTI1) return "Unknown";

    const types = {
      [nifti.NIFTI1.TYPE_UINT8]: "UINT8",
      [nifti.NIFTI1.TYPE_INT16]: "INT16",
      [nifti.NIFTI1.TYPE_INT32]: "INT32",
      [nifti.NIFTI1.TYPE_FLOAT32]: "FLOAT32",
      [nifti.NIFTI1.TYPE_COMPLEX64]: "COMPLEX64",
      [nifti.NIFTI1.TYPE_FLOAT64]: "FLOAT64",
      [nifti.NIFTI1.TYPE_RGB24]: "RGB24",
      [nifti.NIFTI1.TYPE_INT8]: "INT8",
      [nifti.NIFTI1.TYPE_UINT16]: "UINT16",
      [nifti.NIFTI1.TYPE_UINT32]: "UINT32",
      [nifti.NIFTI1.TYPE_INT64]: "INT64",
      [nifti.NIFTI1.TYPE_UINT64]: "UINT64",
      [nifti.NIFTI1.TYPE_FLOAT128]: "FLOAT128",
      [nifti.NIFTI1.TYPE_COMPLEX128]: "COMPLEX128",
      [nifti.NIFTI1.TYPE_COMPLEX256]: "COMPLEX256",
    };

    return types[code] || "Unknown";
  }
}

export default NiftiService;
